/**
 * A smooth route through a run of points.
 *
 * Joining consecutive coins with independent S-curves makes every coin an
 * inflection point: the road straightens to vertical at each one and bends
 * back the other way immediately after, which reads as a mechanical zigzag.
 * A Catmull-Rom spline instead gives each coin a tangent along the direction
 * of travel, so the road flows through it the way a footpath rounds a bend.
 */

/** A point on the canvas. */
export interface SplinePoint {
  x: number;
  y: number;
}

/**
 * Builds one cubic Bezier per consecutive pair of points, with the pieces
 * meeting smoothly (Catmull-Rom, uniform parameterisation).
 *
 * @param points - The route's points in order. Fewer than two yields no
 *     segments.
 * @param tension - How far the control handles reach, as a fraction of the
 *     neighbour-to-neighbour chord. The classic Catmull-Rom value is 1/6.
 *     Larger values bend more lazily; smaller values hug the polyline.
 *     Defaults to 1/6.
 * @return One SVG path string per segment, in order, each starting with a
 *     move to its first point so it can be stroked on its own.
 */
export function splineSegments(
  points: readonly SplinePoint[],
  tension = 1 / 6,
): string[] {
  const segments: string[] = [];
  for (let i = 0; i + 1 < points.length; i += 1) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? points[i + 1];
    const c1 = {
      x: p1.x + (p2.x - p0.x) * tension,
      y: p1.y + (p2.y - p0.y) * tension,
    };
    const c2 = {
      x: p2.x - (p3.x - p1.x) * tension,
      y: p2.y - (p3.y - p1.y) * tension,
    };
    segments.push(
      `M ${p1.x} ${p1.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${p2.x} ${p2.y}`,
    );
  }
  return segments;
}
